import axiosInstance from "../lib/axios.instance";
import { getAccessToken } from "../utils/storage.utils";
import type {
  ICompasAIQueryRequest,
  ICompasAIActionsRequest,
  IActionRunResponse,
  ICompasAIChatHistoryResponse,
  ICompasAIChatAction,
  INextActionResponse,
  IActionOutputResponse,
  IReorderActionsRequest,
  IReorderActionsResponse,
  IEditActionPromptRequest,
  IAnonymizePromptRequest,
  IAnonymizePromptResponse,
  IChatMessagesChainRequest,
  IChatMessagesChainResponse,
} from "../types/compass.types";

const BASE_URL = "compas-ai";

export const compasAIQueryStream = async (
  payload: ICompasAIQueryRequest
): Promise<ReadableStream<Uint8Array>> => {
  const rawUrl = axiosInstance.defaults.baseURL ?? "https://api-dev.legalassistant.au";
  const apiUrl = rawUrl.replace(/\/$/, "");
  const token = await getAccessToken();

  if (token == null) {
    throw new Error("No access token available");
  }

  const response = await fetch(`${apiUrl}/${BASE_URL}/converse/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (response.body == null) {
    throw new Error("Response body is null");
  }

  return response.body;
};

export const getCompassChatHistory = async (
  documentId: string
): Promise<ICompasAIChatHistoryResponse> => {
  const response = await axiosInstance.get<ICompasAIChatHistoryResponse>(
    `${BASE_URL}/history?context_id=${documentId}`
  );
  return response.data;
};

export const runCompassActions = async (
  payload: ICompasAIActionsRequest | ICompasAIActionsRequest[]
): Promise<IActionRunResponse> => {
  const response = await axiosInstance.post<IActionRunResponse>(`${BASE_URL}/actions/run`, payload);
  return response.data;
};

export const getNextAction = async (documentId: string): Promise<INextActionResponse> => {
  const response = await axiosInstance.get<INextActionResponse>(
    `${BASE_URL}/queue/next?context_id=${documentId}`
  );
  return response.data;
};

export const getAllQueuedActions = async (documentId: string): Promise<ICompasAIChatAction[]> => {
  const response = await axiosInstance.get<ICompasAIChatAction[]>(
    `${BASE_URL}/actions?context_id=${documentId}`
  );
  return response.data;
};

export const cancelQueuedActionById = async (
  documentId: string,
  jobIds: string[]
): Promise<void> => {
  const queryParams = new URLSearchParams();
  if (documentId !== "" && jobIds.length > 0) {
    queryParams.append("context_id", documentId);
    queryParams.append("item_ids", jobIds.join(","));
  }
  await axiosInstance.post(`${BASE_URL}/queue/cancel?${queryParams.toString()}`);
};

export const getActionsStatus = async (itemIds: string[]): Promise<Record<string, string>> => {
  const response = await axiosInstance.post<Record<string, string>>(
    `${BASE_URL}/queue/status`,
    itemIds
  );
  return response.data;
};

export const rerunFailedAction = async (itemId: string): Promise<void> => {
  await axiosInstance.post(`${BASE_URL}/actions/rerun/${itemId}`);
};

export const getActionOutput = async (itemId: string): Promise<IActionOutputResponse> => {
  const response = await axiosInstance.get<IActionOutputResponse>(
    `${BASE_URL}/actions/output/${itemId}`
  );
  return response.data;
};

export const reorderActions = async (
  payload: IReorderActionsRequest
): Promise<IReorderActionsResponse> => {
  const { context_id, ...requestBody } = payload;
  const response = await axiosInstance.post<IReorderActionsResponse>(
    `${BASE_URL}/history/reorder-actions?context_id=${context_id}`,
    requestBody
  );
  return response.data;
};

export const editActionPrompt = async (
  actionId: string,
  contextId: string,
  prompt: string
): Promise<void> => {
  const payload: IEditActionPromptRequest = { prompt };
  await axiosInstance.post(
    `${BASE_URL}/actions/edit-prompt/${actionId}?context_id=${contextId}`,
    payload
  );
};

export const deleteAction = async (actionId: string, contextId: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/actions/${actionId}?context_id=${contextId}`);
};

export const anonymizePrompt = async (
  payload: IAnonymizePromptRequest
): Promise<IAnonymizePromptResponse> => {
  const response = await axiosInstance.post<IAnonymizePromptResponse>(
    `${BASE_URL}/anonymize-prompt`,
    payload
  );
  return response.data;
};

export const chatMessagesChain = async (
  payload: IChatMessagesChainRequest
): Promise<IChatMessagesChainResponse> => {
  const response = await axiosInstance.post<IChatMessagesChainResponse>(
    `ai/chat/messages/chain`,
    payload
  );
  return response.data;
};
